import _ from 'lodash';

import type { AwardRequirement } from '@appTypes/esb';
import { parseStringBoolean } from '@modules/shared/utils/parsers';
import { shouldRequirementDisplayOnly } from '@modules/youthProfile/utils';
import { awardRequirementIdsWithoutApproveOption } from '@shared/constants';

import type { AdvancementRequirement, RankRequirement } from '../types';

export function sortBulkRequirements<T extends AdvancementRequirement>(
  requirements: T[],
): T[] {
  if (!requirements) return [];
  const containsSortOrder = requirements.some(req => req.sortOrder);

  /**
   * Ranks API already contain sort order information, e.g.
   * parent: { id: 123, sortOrder: "1", listNumber: "1.", requirementNumber: "" }
   * children: { parentRequirementId: 123, sortOrder: "1.1", listNumber: "(a)", requirementNumber: "1a" }
   *
   */
  if (containsSortOrder) {
    const sorted = _.chain(requirements)
      .map((item, i) => {
        const numberValue = Number(item.sortOrder);
        const isNaN = Number.isNaN(numberValue);
        let sortValue = isNaN ? item.sortOrder : numberValue;
        if (item.parentRequirementId && !item.sortOrder) {
          sortValue = Number(requirements.find(item => item.id)?.sortOrder) + i;
        }
        return { ...item, sortValue };
      })
      .orderBy(['sortValue'], ['asc'])
      .map(item => _.omit(item, ['sortValue']))
      .value() as unknown as T[];
    return sorted;
  }

  const childrenRequirements = requirements.filter(
    req => !!req.parentRequirementId,
  );
  const parentRequirements = requirements.filter(
    req =>
      !req.parentRequirementId ||
      childrenRequirements.some(req => req.parentRequirementId === req.id),
  );

  /**
   * V1 APIs doesn't have a sortOrder property, for children reqs i.e. 1a,1b, they look like:
   *
   * parent: { id: 123, listNumber: "1", requirementNumber: "" }
   * children: { parentRequirementId: 123, listNumber: "(a)", requirementNumber: "1a" }
   */
  //
  const sorted = _.chain(parentRequirements)
    // First sort parent reqs by listNumber e.g. 1,2,3...
    .orderBy(
      req => {
        const value = Number(req.listNumber);
        if (Number.isNaN(value)) return req.listNumber;
        return value;
      },
      ['asc'],
    )
    .map(req =>
      // then sort children requirements by requirementNumber 1a,1b,1c,etc
      [
        req,
        ..._.chain(childrenRequirements)
          .filter(childrenReq => childrenReq.parentRequirementId === req.id)
          .orderBy('requirementNumber', ['asc'])
          .value(),
      ],
    )
    .flatMap()
    .value() as unknown as T[];
  return sorted;
}

export function isParentRequirement(requirement: AdvancementRequirement) {
  return Boolean(requirement.childrenRequired);
}

export function isUpdateableRequirement(requirement: AdvancementRequirement) {
  const isParent = isParentRequirement(requirement);
  const rankReq = requirement as RankRequirement;

  const hasLinkedMeritBadges = [
    Number(rankReq.eagleMBRequired),
    Number(rankReq.totalMBRequired),
  ].some(Boolean);

  const isSeaScoutElective =
    rankReq.requiresSSElective || !_.isEmpty(rankReq.ssElectives);

  const hasLinkedAdventures = ![
    rankReq.linkedAdventure,
    rankReq.linkedAdventureId,
    rankReq.linkedAwardId,
    rankReq.linkedElectiveAdventures,
  ].every(_.isEmpty);
  const disabledOnQuickEntry = parseStringBoolean(rankReq.disabledOnQuickEntry);

  const isUpdateable =
    !isSeaScoutElective &&
    !hasLinkedMeritBadges &&
    !isParent &&
    !hasLinkedAdventures &&
    !disabledOnQuickEntry;

  return isUpdateable;
}

export function isInvalidAwardRequirement(requirement: AwardRequirement) {
  return awardRequirementIdsWithoutApproveOption.includes(requirement.id);
}

export function isNotUpdateableRequirement(
  requirement: AdvancementRequirement,
) {
  const isNotUpdateableRequirement =
    !isUpdateableRequirement(requirement) ||
    shouldRequirementDisplayOnly(requirement);

  return isNotUpdateableRequirement;
}
